import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardMedia, 
  CardContent, 
  Grid, 
  Button, 
  CircularProgress, 
  Alert, 
  Chip,
  IconButton,
  CardActions,
  Tooltip
} from '@mui/material';
import { 
  PlayArrow, 
  QrCode as QrCodeIcon,
  Visibility as ViewIcon,
  Share as ShareIcon 
} from '@mui/icons-material';
import YoutubeVideoPlayer from '../components/YoutubeVideoPlayer';
import customYoutubeApi from '../api/customYoutubeApi';
import './YoutubeVideosPage.css';

const CustomYoutubeVideosPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videosWithThumbnails, setVideosWithThumbnails] = useState([]);
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, [token]);

  const checkBestThumbnail = async (video) => {
    const maxResUrl = `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`;
    const hqUrl = `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;
    
    try {
      // Try maxresdefault first
      const response = await fetch(maxResUrl, { method: 'HEAD' });
      if (response.ok) {
        return { ...video, thumbnailUrl: maxResUrl, thumbnailQuality: 'maxres' };
      }
    } catch (error) {
      console.log(`Maxres thumbnail not available for ${video.title}`);
    }
    
    // Fallback to hqdefault
    return { ...video, thumbnailUrl: hqUrl, thumbnailQuality: 'hq' };
  };

  const fetchVideos = async () => {
    try {
      const response = await customYoutubeApi.getAll();
      setVideos(response.data);
      setLoading(false);
      
      // Start loading thumbnails asynchronously
      setThumbnailsLoading(true);
      console.log('Checking thumbnail availability for', response.data.length, 'videos...');
      
      const videosWithThumbnailPromises = response.data.map(video => checkBestThumbnail(video));
      const videosWithThumbnailData = await Promise.all(videosWithThumbnailPromises);
      
      setVideosWithThumbnails(videosWithThumbnailData);
      setThumbnailsLoading(false);
      console.log('Thumbnails loaded successfully');
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError(err.message);
      setLoading(false);
      setThumbnailsLoading(false);
    }
  };

  const handleVideoSelect = (video) => {
    if (video.customVideoId) {
      // Navigate to the dedicated video viewer page for better user experience
      navigate(`/custom-youtube-video/${video.customVideoId}`);
    } else {
      // Fallback for videos without customVideoId
      setSelectedVideo(video);
    }
  };

  const handleShare = async (video) => {
    if (video.customVideoId) {
      const url = `${window.location.origin}/custom-youtube-video/${video.customVideoId}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: video.title,
            text: video.description || `Watch "${video.title}"`,
            url: url
          });
        } catch (err) {
          if (err.name !== 'AbortError') {
            copyToClipboard(url);
          }
        }
      } else {
        copyToClipboard(url);
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copied to clipboard!');
    });
  };

  const handleBackToList = () => {
    setSelectedVideo(null);
  };

  const formatTime = (seconds) => {
    const totalSeconds = Math.floor(seconds); // Round down to nearest whole second
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading videos: {error}
        </Alert>
      </Box>
    );
  }

  if (selectedVideo) {
    return (
      <Box sx={{ p: 3 }}>
        <YoutubeVideoPlayer 
          video={selectedVideo} 
          onBack={handleBackToList}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
        📺 Custom YouTube Videos
      </Typography>
      
      <Typography variant="h6" component="p" align="center" sx={{ mb: 4, color: 'text.secondary' }}>
        Select a video to watch with custom playback controls
      </Typography>
      
      {videos.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No custom playback videos available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Videos need to have at least startPoint and endPoint1 configured
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {(videosWithThumbnails.length > 0 ? videosWithThumbnails : videos).map((video) => {
            const thumbnailUrl = video.thumbnailUrl || `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;
            const hasEndPoint2 = video.endPoint2 !== undefined && video.endPoint2 !== null;
            return (
            <Grid item xs={12} sm={6} md={4} key={video._id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6
                  }
                }}
                onClick={() => handleVideoSelect(video)}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={thumbnailUrl}
                    alt={video.title || video.name}
                    onError={(e) => {
                      console.log(`Image failed to load for ${video.title}`);
                      // Fallback to a placeholder if even the pre-checked thumbnail fails
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjY2NjIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNHB4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                    }}
                    sx={{
                      objectFit: 'cover',
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    <PlayArrow 
                      sx={{ 
                        fontSize: 60, 
                        color: 'white',
                        backgroundColor: 'rgba(255, 0, 0, 0.8)',
                        borderRadius: '50%',
                        p: 1
                      }} 
                    />
                  </Box>
                </Box>
                
                <CardContent>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {video.title || video.name}
                  </Typography>
                  
                  {video.description && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {video.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip 
                      label={`Start: ${formatTime(video.startPoint)}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip 
                      label={`End 1: ${formatTime(video.endPoint1)}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                    {hasEndPoint2 && (
                      <Chip 
                        label={`End 2: ${formatTime(video.endPoint2)}`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {!hasEndPoint2 && (
                      <Chip 
                        label="Single endpoint"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  
                  {video.tags && video.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {video.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                      {video.tags.length > 3 && (
                        <Chip label={`+${video.tags.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button 
                      size="small" 
                      startIcon={<PlayArrow />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVideoSelect(video);
                      }}
                    >
                      Play
                    </Button>
                    
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {video.customVideoId && (
                        <>
                          <Tooltip title="View Full Page">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/custom-youtube-video/${video.customVideoId}`);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="QR Code">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/custom-youtube-video/${video.customVideoId}/qr`);
                              }}
                            >
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Share">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(video);
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default CustomYoutubeVideosPage;
